import { useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  { num: '01', title: 'Add', icon: '◇' },
  { num: '02', title: 'Learn', icon: '◈' },
  { num: '03', title: 'Daily', icon: '▫' },
  { num: '04', title: 'Invite', icon: '◎' },
  { num: '05', title: 'Shop', icon: '▪' },
];

export const BetaGate = ({ onUnlocked }) => {
  const { isAuthenticated, checkAuth } = useAuth();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistName, setWaitlistName] = useState('');

  const redeem = async (e) => {
    e?.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) { toast.error('Enter your invite code.'); return; }
    
    setBusy(true);
    try {
      const endpoint = isAuthenticated ? '/api/beta-gate/claim' : '/api/beta-gate/redeem-code';
      await axios.post(`${window.__SG_API_BASE__}${endpoint}`, { code: trimmed }, { withCredentials: true });
      
      toast.success('Welcome to StyleGenome.');
      if (isAuthenticated) {
        await checkAuth(); // Refresh user role
      }
      onUnlocked?.(trimmed);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code. Try again or request access.');
    } finally {
      setBusy(false);
    }
  };

  const joinWaitlist = async (e) => {
    e?.preventDefault();
    if (!waitlistEmail.trim()) { toast.error('Email is required.'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${window.__SG_API_BASE__}/api/waitlist`, {
        email: waitlistEmail.trim(),
        name: waitlistName.trim() || undefined,
      }, { withCredentials: true });
      
      if (res.data.status === "approved") { toast.success("Success. You have been granted early access."); onUnlocked?.(); } else { toast.success("You're on the list."); }
      setWaitlistEmail(''); 
      setWaitlistName(''); 
      setShowWaitlist(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join the waitlist.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#F7F5F1] flex flex-col font-sans" data-testid="beta-gate">
      <header className="px-6 py-6 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <Logo className="w-8 h-8" variant="mark" />
          <span className="font-serif text-lg tracking-wide">StyleGenome</span>
        </div>
        <span className="text-[9px] uppercase tracking-[0.35em] text-[#C7A96B]/80">Invite only</span>
      </header>

      <section className="flex-1 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12 max-w-2xl mx-auto w-full">
        <p className="text-[9px] uppercase tracking-[0.4em] text-[#C7A96B] mb-6">Private beta</p>
        <h1 className="font-serif text-[2.5rem] sm:text-5xl lg:text-6xl leading-[1.08] mb-5">
          Your style,<br />refined by AI.
        </h1>
        <p className="text-[#F7F5F1]/60 text-sm leading-relaxed mb-10 max-w-sm">
          AI-assisted style, closet, and shopping guidance. Access is expanding in waves.
        </p>

        <form onSubmit={redeem} className="mb-4 flex gap-3 max-w-sm">
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder="Invite code"
            className="flex-1 bg-transparent border-b border-[#F7F5F1]/25 px-0 py-3 text-[#F7F5F1] placeholder:text-[#8E8A84]/60 text-sm focus:outline-none focus:border-[#C7A96B] transition-colors"
            autoComplete="off"
          />
          <button type="submit" disabled={busy} className="px-8 py-3 bg-[#C7A96B] text-[#0D0D0D] text-[11px] uppercase tracking-[0.2em] hover:bg-[#D4B57A] transition-colors disabled:opacity-50 font-medium">
            {busy ? '...' : 'Enter'}
          </button>
        </form>
        <div className="mt-8 flex flex-col gap-4 max-w-sm">
           <div className="relative flex items-center justify-center">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#F7F5F1]/10"></div></div>
              <span className="relative bg-[#0D0D0D] px-4 font-sans text-[9px] uppercase tracking-widest text-[#8E8A84]">or</span>
           </div>
           <div className="flex gap-4">
              <button onClick={() => window.location.href = "/login"} className="flex-1 border border-[#F7F5F1]/20 py-3 rounded-full font-sans text-[10px] uppercase tracking-widest hover:bg-white/5 transition-colors">
                Sign In
              </button>
              <button onClick={() => window.location.href = "/signup"} className="flex-1 border border-[#F7F5F1]/20 py-3 rounded-full font-sans text-[10px] uppercase tracking-widest hover:bg-white/5 transition-colors">
                Join
              </button>
           </div>
        </div>

        
        <button type="button" onClick={() => setShowWaitlist(s => !s)} className="text-[10px] uppercase tracking-[0.25em] text-[#8E8A84]/70 hover:text-[#F7F5F1] transition-colors self-start mt-2">
          {showWaitlist ? 'Hide' : 'Request access'} →
        </button>

        {showWaitlist && (
          <form onSubmit={joinWaitlist} className="mt-8 flex flex-col gap-4 max-w-sm animate-fade-in">
            <input type="email" required placeholder="Email" value={waitlistEmail}
              onChange={e => setWaitlistEmail(e.target.value)}
              className="bg-transparent border-b border-[#F7F5F1]/20 px-0 py-2.5 text-sm placeholder:text-[#8E8A84]/50 focus:outline-none focus:border-[#C7A96B] transition-colors" />
            <input type="text" placeholder="Name" value={waitlistName}
              onChange={e => setWaitlistName(e.target.value)}
              className="bg-transparent border-b border-[#F7F5F1]/20 px-0 py-2.5 text-sm placeholder:text-[#8E8A84]/50 focus:outline-none focus:border-[#C7A96B] transition-colors" />
            <button type="submit" disabled={busy} className="self-start mt-2 px-6 py-2.5 border border-[#C7A96B]/60 text-[#C7A96B] text-[10px] uppercase tracking-[0.2em] hover:bg-[#C7A96B] hover:text-[#0D0D0D] transition-all disabled:opacity-50">
              Join Waitlist
            </button>
          </form>
        )}
      </section>

      <section className="py-12 border-t border-white/5">
        <p className="text-[9px] uppercase tracking-[0.35em] text-[#C7A96B]/70 px-6 md:px-12 mb-8 text-center md:text-left">How it works</p>
        <div className="flex gap-6 overflow-x-auto px-6 md:px-12 pb-6 snap-x snap-mandatory no-scrollbar">
          {STEPS.map((step) => (
            <article key={step.num} className="snap-start shrink-0 w-[160px] border border-white/10 p-6 flex flex-col gap-4 hover:border-[#C7A96B]/40 transition-colors group">
              <span className="text-2xl opacity-40 group-hover:opacity-100 transition-opacity">{step.icon}</span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-[#C7A96B]/60">{step.num}</span>
              <span className="font-serif text-base text-[#F7F5F1]/90 leading-snug">{step.title}</span>
            </article>
          ))}
        </div>
      </section>

      <footer className="px-6 md:px-12 py-8 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex flex-wrap justify-center gap-6 text-[9px] uppercase tracking-[0.25em] text-[#8E8A84]/60">
          <span>Private by default</span>
          <span>AI Assisted</span>
          <span>Invite Only</span>
        </div>
        <div className="flex items-center gap-8 text-[9px] uppercase tracking-[0.2em] text-[#8E8A84]/40">
          <a href="/privacy" className="hover:text-[#F7F5F1]">Privacy</a>
          <a href="/terms" className="hover:text-[#F7F5F1]">Terms</a>
          <a href="/ai-disclosure" className="hover:text-[#F7F5F1]">AI</a>
        </div>
      </footer>
    </div>
  );
};

export default BetaGate;
